import { CARD_GAMES, getGameName, getGameSlug } from './games';

export interface HowToPlayStep {
  step: number;
  name: string;
  isDashed?: boolean;
  desc?: string;
}

export interface HowToPlayGlossaryItem {
  term: string;
  definition: string;
}

export interface GameQuickGuide {
  players: string | number;
  handSize: string | number;
  victory: string;
  deckSize?: string;
}

export interface GameTurnPhase {
  phase: string;
  name: string;
  description: string;
}

export interface GameZone {
  name: string;
  description: string;
}

export interface GameReferences {
  official_rules_url?: string | null;
  tutorial_title?: string | null;
  tutorial_url?: string | null;
}

export interface GameRuleDetail {
  id: number;
  slug: string;
  name: string;
  gameName: string;
  title: string;
  lead: string;
  subtitle?: string;
  quickGuide: GameQuickGuide;
  turnSteps: HowToPlayStep[];
  turnSubtext?: string;
  objective: string;
  preparation: string;
  onYourTurn: string;
  howToWin: string;
  glossary: HowToPlayGlossaryItem[];
  formats: string[];
  cardTypes: string[];
  winConditions?: string[];
  initialSetup?: {
    handSize: number;
    initialLifeOrPoints?: string | number;
    mulliganRule?: string;
    specialSetup?: string;
  };
  turnPhases?: GameTurnPhase[];
  zones?: GameZone[];
  references?: GameReferences;
}

export type HowToPlayGuide = GameRuleDetail;

export const GAME_RULES: Record<string, GameRuleDetail> = {
  'pokemon': {
    id: 3,
    slug: 'pokemon',
    name: 'Pokémon TCG',
    gameName: 'Pokémon',
    title: 'Como jogar Pokémon',
    subtitle: 'Treine, evolua e batalhe com seus Pokémon preferidos',
    lead: 'No Pokémon Estampas Ilustradas, dois Treinadores batalham com seus Pokémon até alguém pegar todas as cartas de Prêmio. É fácil de aprender e cheio de estratégia — e você pode treinar agora mesmo, de graça, no simulador, sem precisar das cartas físicas.',
    quickGuide: {
      players: 2,
      handSize: 7,
      victory: 'Pegar os 6 Prêmios',
      deckSize: '60 cartas'
    },
    turnSteps: [
      { step: 1, name: 'Comprar', desc: 'Compre 1 carta do seu baralho no início do turno (obrigatório, salvo efeito de cartas).' },
      { step: 2, name: 'Ações', desc: 'Em qualquer ordem: coloque Pokémon Básicos no Banco, evolua Pokémon, ligue 1 Energia, jogue Treinadores, recue o Pokémon Ativo e use Habilidades.' },
      { step: 3, name: 'Atacar', desc: 'Declare o ataque do Pokémon Ativo pagando as Energias do custo. O ataque causa dano, aplica fraqueza/resistência e encerra o seu turno.' },
      { step: 4, name: 'Checape Pokémon', isDashed: true, desc: 'As etapas tracejadas costumam ser rápidas e, na maioria das vezes, podem ser puladas. É onde se aplicam Condições Especiais, como o dano de Veneno.' }
    ],
    turnSubtext: 'As etapas tracejadas costumam ser rápidas e, na maioria das vezes, podem ser puladas.',
    objective: 'Você vence ao cumprir qualquer uma destas condições: pegar todas as suas 6 cartas de Prêmio, deixar o oponente sem nenhum Pokémon em jogo, ou fazer o oponente ficar sem cartas no baralho no início do turno dele.',
    preparation: 'Jogue uma moeda: quem vencer decide quem joga primeiro. Embaralhe seu baralho de 60 cartas e compre 7. Coloque 1 Pokémon Básico virado para baixo como seu Pokémon Ativo e até 5 no Banco. Separe as 6 cartas do topo como Prêmios, viradas para baixo. Depois, os dois desviram seus Pokémon e começam.',
    onYourTurn: 'Compre 1 carta. Depois, em qualquer ordem: coloque Pokémon Básicos no Banco, evolua Pokémon, ligue 1 Energia, jogue cartas de Treinador (só 1 Apoiador e 1 Estádio por turno), recue o Pokémon Ativo uma vez e use Habilidades. Para encerrar, ataque com seu Pokémon Ativo. No primeiro turno, quem joga primeiro não ataca nem joga Apoiador.',
    howToWin: 'Ao atacar, confira se o Pokémon Ativo tem a Energia do custo, aplique Fraqueza e Resistência e coloque 1 contador de dano para cada 10 de dano. Se o dano chegar ao PS, o Pokémon é Nocauteado e você pega uma carta de Prêmio (2 por um Pokémon ex, 3 por um Pokémon ex de Megaevolução). O dono escolhe um novo Pokémon Ativo no Banco. Quem pega todos os Prêmios primeiro vence.',
    glossary: [
      { term: 'Baralho', definition: 'Exatamente 60 cartas, com no máximo 4 cópias de cada nome, exceto Energia Básica.' },
      { term: 'Pokémon Básico', definition: 'Pokémon que entra em jogo direto da mão, sem precisar evoluir.' },
      { term: 'Pokémon Ativo', definition: 'O Pokémon na linha de frente, que ataca e recebe os ataques. Só pode haver um.' },
      { term: 'Treinador', definition: 'Cartas de Item, Ferramenta Pokémon, Apoiador e Estádio, com efeitos variados.' },
      { term: 'Banco', definition: 'Onde ficam até 5 Pokémon na reserva, prontos para entrar na batalha.' },
      { term: 'Apoiador e Estádio', definition: 'Cartas de Treinador limitadas a uma de cada por turno. O Estádio fica em jogo, e só pode haver um na mesa.' },
      { term: 'Nocaute', definition: 'Quando o dano num Pokémon chega ao PS dele, ele e as cartas ligadas a ele vão para a pilha de descarte.' },
      { term: 'Condições Especiais', definition: 'Adormecido, Confuso, Envenenado, Paralisado e Queimado. Só afetam o Pokémon Ativo.' },
      { term: 'Recuar', definition: 'Trocar o Pokémon Ativo por um do Banco, descartando Energia conforme o Custo de Recuo. Uma vez por turno.' },
      { term: 'Checape Pokémon', definition: 'Etapa entre os turnos em que se aplicam as Condições Especiais, como o dano de Veneno.' },
      { term: 'Evoluir', definition: 'Colocar uma carta de Evolução sobre o Pokémon correspondente. Não vale no primeiro turno do jogo nem no Pokémon que acabou de entrar em jogo.' },
      { term: 'Nova mão inicial (mulligan)', definition: 'Sem Pokémon Básico na mão inicial, mostre-a, embaralhe no baralho e compre 7 de novo. O oponente pode comprar 1 carta extra por vez.' }
    ],
    formats: ['Casual', 'Expanded', 'Standard'],
    cardTypes: [
      'Baby', 'Básico', 'BREAK', 'Energia Básica', 'Energia Especial', 'Estádio',
      'EX', 'GX', 'Item', 'Lenda', 'Level-Up', 'Mega', 'Pokémon Tool', 'Restored',
      'Stage 1', 'Stage 2', 'Suporte', 'Tag Team', 'Technical Machine', 'Treinador',
      'V', 'VMAX'
    ],
    winConditions: [
      'Comprar todas as suas 6 Prize Cards após nocautear Pokémon adversários.',
      'O oponente não possuir nenhum Pokémon em campo (nem Ativo nem no Banco).',
      'O oponente não conseguir comprar uma carta no início de seu turno.'
    ],
    turnPhases: [
      { phase: 'draw', name: 'Draw Step', description: 'Compre 1 carta do deck.' },
      { phase: 'main', name: 'Main Actions', description: 'Coloque Básicos no banco, evolua, ligue 1 Energia e jogue Treinadores.' },
      { phase: 'attack', name: 'Attack Step', description: 'Ataque com o Pokémon Ativo, aplique fraqueza e pegue Prêmios se houver KO.' }
    ],
    zones: [
      { name: 'Active Spot', description: 'Posição do Pokémon Ativo na linha de frente.' },
      { name: 'Bench', description: 'Banco com até 5 Pokémon na reserva.' },
      { name: 'Prize Cards', description: '6 cartas de recompensa por nocautes.' },
      { name: 'Deck', description: 'Baralho de 60 cartas exatas.' },
      { name: 'Discard Pile', description: 'Pilha de descarte.' }
    ]
  },

  'one-piece': {
    id: 1,
    slug: 'one-piece',
    name: 'One Piece Card Game',
    gameName: 'One Piece',
    title: 'Como jogar One Piece',
    subtitle: 'Torne-se o Rei dos Piratas duelando com sua tripulação e líder',
    lead: 'No One Piece Card Game, você lidera sua própria tripulação pirata comandada por um Líder emblemático. Enfrente oponentes em batalhas intensas usando recursos dinâmicos do baralho DON!! e conquiste a glória nos mares da Grand Line.',
    quickGuide: {
      players: 2,
      handSize: 5,
      victory: 'Destruir todas as Vidas do Líder',
      deckSize: '50 cartas + 1 Líder + 10 DON!!'
    },
    turnSteps: [
      { step: 1, name: 'Refresh', desc: 'Desvire todos os seus Personagens e retorne os DON!! anexados aos seus combatentes para a Cost Area.' },
      { step: 2, name: 'Draw', desc: 'Compre 1 carta do seu deck (o jogador que começa no primeiro turno não compra).' },
      { step: 3, name: 'DON!!', desc: 'Coloque 2 cartas do DON!! Deck na Cost Area na posição ativa (1 se for o 1º turno do jogador inicial).' },
      { step: 4, name: 'Main Phase', desc: 'Gaste DON!! para jogar Personagens, Eventos ou cartas de Stage, anexe DON!! ativos (+1000 poder) e declare ataques.' },
      { step: 5, name: 'End Phase', isDashed: true, desc: 'A fase End resolve efeitos contínuos de encerramento e passa a vez imediatamente.' }
    ],
    turnSubtext: 'A fase End resolve efeitos contínuos de encerramento e passa a vez imediatamente.',
    objective: 'Você vence a partida reduzindo a 0 as cartas de Vida do Líder adversário e acertando um ataque final direto bem-sucedido contra ele, ou se o oponente não conseguir comprar cartas do baralho.',
    preparation: 'Coloque seu Líder virado para cima na Leader Area. Embaralhe seu baralho principal de 50 cartas e separe 10 cartas no DON!! Deck viradas para baixo. Decida quem começa com pedra-papel-tesoura ou dado. Compre 5 cartas. Pode fazer mulligan (reembaralhar as 5 e comprar 5 novas apenas 1 vez). Coloque tantas cartas de Vida viradas para baixo quantas forem indicadas no seu Líder.',
    onYourTurn: 'Na Refresh Phase, desvire seus Personagens e devolva os DON!! anexados para a Cost Area. Compre 1 carta (exceto jogador 1 no 1º turno). Na fase DON!!, coloque 2 DON!! ativos da pilha na Cost Area (1 no primeiro turno do jogador 1). Na Main Phase, gaste DON!! para jogar Personagens, Eventos ou cartas de Stage, anexe DON!! ativos aos seus lutadores (+1000 poder cada) e ataque!',
    howToWin: 'Ataque o Líder ou Personagens suspensos (virados) do oponente. Durante a batalha, o defensor pode usar cartas de Counter da mão (+1000/+2000) ou ativar Personagens com efeito Blocker. Se o poder do atacante for igual ou maior, o ataque tem sucesso. Derrote as vidas do Líder até o 0 e acerte o golpe final!',
    glossary: [
      { term: 'Líder (Leader)', definition: 'A carta principal do seu baralho que dita suas cores, vida inicial e habilidades de comando.' },
      { term: 'DON!! Deck', definition: 'Pilha separada com 10 cartas DON!! que funcionam como mana/recurso e bônus de ataque (+1000 de poder por DON!! anexado).' },
      { term: 'Personagem (Character)', definition: 'Membros da sua tripulação invocados no campo para atacar, bloquear e usar habilidades.' },
      { term: 'Stage', definition: 'Locais icônicos que permanecem em campo fornecendo benefícios permanentes para a sua tripulação.' },
      { term: 'Blocker (Bloqueador)', definition: 'Habilidade que permite virar o Personagem para redirecionar um ataque adversário para si.' },
      { term: 'Trigger (Gatilho)', definition: 'Efeito impresso em certas cartas de Vida que pode ser ativado gratuitamente assim que a carta é sofrida como dano.' },
      { term: 'Counter', definition: 'Cartas ou personagens descartados da mão durante o turno adversário para somar +1000 ou +2000 de poder defensivo.' },
      { term: 'Trash (Descarte)', definition: 'Cemitério onde ficam personagens derrotados e eventos concluídos.' }
    ],
    formats: ['Standard', 'Sealed / Pré-lançamento'],
    cardTypes: ['Líder', 'Personagem', 'Evento', 'Stage', 'DON!!'],
    winConditions: [
      'Atacar com sucesso o Líder adversário quando ele não tiver mais cartas de Vida.',
      'O oponente ficar sem cartas no deck no momento obrigatório de comprar.'
    ],
    zones: [
      { name: 'Leader Area', description: 'Onde o seu Líder permanece durante toda a partida.' },
      { name: 'Character Area', description: 'Espaço para até 5 Personagens simultâneos.' },
      { name: 'Stage Area', description: 'Espaço para 1 carta de Stage ativa por jogador.' },
      { name: 'Cost Area', description: 'Onde ficam os DON!! disponíveis para pagar custos.' },
      { name: 'DON!! Deck', description: 'Pilha com 10 cartas DON!!.' },
      { name: 'Deck Area', description: 'Seu baralho principal de 50 cartas.' },
      { name: 'Trash', description: 'Pilha de descarte.' },
      { name: 'Life Area', description: 'Cartas viradas para baixo representando as vidas do Líder.' }
    ]
  },

  'magic': {
    id: 4,
    slug: 'magic',
    name: 'Magic: The Gathering',
    gameName: 'Magic: The Gathering',
    title: 'Como jogar Magic: The Gathering',
    subtitle: 'Invoque criaturas lendárias e lance magias cósmicas através do multiverso',
    lead: 'Em Magic: The Gathering, você é um Planeswalker poderoso duelando em multiversos místicos. Lance criaturas mitológicas, feitiços cósmicos e artefatos lendários usando o fluxo de mana extraído dos seus terrenos.',
    quickGuide: {
      players: '2 a 4',
      handSize: 7,
      victory: 'Zerar os 20 pontos de Vida',
      deckSize: '60+ (ou 100 em Commander)'
    },
    turnSteps: [
      { step: 1, name: 'Início (Untap/Upkeep/Draw)', desc: 'Desvire suas permanentes viradas (Untap), resolva habilidades de manutenção (Upkeep) e compre 1 carta (Draw).' },
      { step: 2, name: 'Fase Principal 1', desc: 'Baixe até 1 terreno da mão e conjure criaturas, feitiços, artefatos ou planeswalkers virando terrenos para gerar mana.' },
      { step: 3, name: 'Combate', desc: 'Declare criaturas atacantes; o defensor escolhe os bloqueadores e o dano de combate é resolvido simultaneamente.' },
      { step: 4, name: 'Fase Principal 2', desc: 'Segunda oportunidade para baixar terreno (se ainda não o fez), conjurar novas criaturas e preparar defesas.' },
      { step: 5, name: 'Final (Limpeza)', isDashed: true, desc: 'A etapa de limpeza descarta para o limite de 7 cartas e remove danos das criaturas.' }
    ],
    turnSubtext: 'A etapa de limpeza descarta para o limite de 7 cartas e remove danos das criaturas.',
    objective: 'Reduzir a pontuação de vida do oponente de 20 (ou 40 em Commander) a 0, acumular 10 marcadores de veneno nele, ou fazer com que ele compre de um grimório vazio.',
    preparation: 'Embaralhe seu grimório e compre 7 cartas. Aplique o London Mulligan se necessário (compre 7 e devolva 1 ao fundo por mulligan realizado). Sorteie quem começa. O jogador inicial pula a etapa de compra do seu primeiro turno.',
    onYourTurn: 'Desvire todas as suas permanentes viradas, resolva a manutenção e compre 1 carta. Na Fase Principal 1, você pode descer até 1 terreno da mão e conjurar criaturas, feitiços, encantamentos ou artefatos gerando mana ao virar seus terrenos. Na fase de combate, declare atacantes virando suas criaturas; o defensor escolhe os bloqueadores.',
    howToWin: 'Ataque diretamente os pontos de vida do adversário com criaturas não bloqueadas. Use magias instantâneas para surpreender em combate e controle a mesa com remoções e sinergias até esgotar a defesa inimiga.',
    glossary: [
      { term: 'Grimório (Library)', definition: 'Seu baralho de cartas virado para baixo de onde você compra a cada turno.' },
      { term: 'Terreno (Land)', definition: 'Cartas que geram Mana de cinco cores (Branco, Azul, Preto, Vermelho, Verde) ou Incolor.' },
      { term: 'Virar / Desvirar (Tap / Untap)', definition: 'Girar a carta em 90 graus para indicar que ela foi usada para gerar mana ou atacar.' },
      { term: 'Enjoo de Invocação', definition: 'Criaturas recém-invocadas não podem atacar nem usar habilidades com símbolo de virar no mesmo turno, salvo com Ímpeto (Haste).' },
      { term: 'A Pilha (The Stack)', definition: 'Zona mágica onde mágicas e habilidades aguardam resolução do topo para a base (última a entrar, primeira a sair).' },
      { term: 'Cemitério (Graveyard)', definition: 'Pilha de descarte de mágicas resolvidas e permanentes destruídas.' },
      { term: 'Comandante', definition: 'Em formato Commander, criatura lendária central que pode ser invocada repetidamente da Zona de Comando.' },
      { term: 'London Mulligan', definition: 'Regra de mulligan onde você compra 7 cartas completas e devolve cartas excedentes ao fundo do baralho.' }
    ],
    formats: ['Commander', 'Standard', 'Modern', 'Pauper', 'Pioneer', 'Legacy', 'Vintage'],
    cardTypes: ['Criatura', 'Terreno', 'Feitiço', 'Mágica Instantânea', 'Artefato', 'Encantamento', 'Planeswalker', 'Batalha']
  },

  'yugioh': {
    id: 9,
    slug: 'yugioh',
    name: 'Yu-Gi-Oh!',
    gameName: 'Yu-Gi-Oh!',
    title: 'Como jogar Yu-Gi-Oh!',
    subtitle: 'Duelos lendários de monstros, magias e armadilhas',
    lead: 'O lendário jogo de cartas de Kazuki Takahashi coloca duelistas frente a frente em combates monumentais de monstros, magias de campo e armadilhas sorrateiras.',
    quickGuide: {
      players: 2,
      handSize: 5,
      victory: 'Zerar os 8000 Pontos de Vida (LP)',
      deckSize: '40 a 60 cartas'
    },
    turnSteps: [
      { step: 1, name: 'Draw Phase', desc: 'Compre 1 carta do topo do Main Deck (o jogador inicial não compra no 1º turno).' },
      { step: 2, name: 'Standby Phase', isDashed: true, desc: 'A Standby Phase costuma ser rápida, resolvendo gatilhos automáticos e custos de manutenção.' },
      { step: 3, name: 'Main Phase 1', desc: 'Faça sua Invocação-Normal (ou por Tributo), Invocação-Especial, ative Magias e baixe Armadilhas viradas para baixo.' },
      { step: 4, name: 'Battle Phase', desc: 'Declare ataques com monstros em Posição de Ataque contra monstros adversários ou diretamente nos pontos de vida.' },
      { step: 5, name: 'Main Phase 2', desc: 'Ajuste seu campo após a batalha, baixe novas defesas e prepare-se para o turno do adversário.' },
      { step: 6, name: 'End Phase', isDashed: true, desc: 'Efeitos temporários expiram e aplica-se o limite de mão de 6 cartas descartando o excesso.' }
    ],
    turnSubtext: 'A Standby e a End Phase costumam ser rápidas, resolvendo gatilhos automáticos.',
    objective: 'Reduzir os Life Points (LP) do rival de 8000 a 0, esgotar as cartas do baralho dele (Deck Out), ou completar condições de vitória automática como as cinco partes do Exodia.',
    preparation: 'Construa seu Main Deck (40-60 cartas), Extra Deck (até 15 cartas de Fusão, Synchro, Xyz, Link) e Side Deck (até 15 cartas). Ambos embaralham, cortam e compram 5 cartas. Decida quem começa com moeda ou dado. O primeiro duelista não compra carta no primeiro turno e não pode declarar batalha.',
    onYourTurn: 'Compre 1 carta na Draw Phase. Na Main Phase 1, você tem direito a 1 Invocação-Normal de monstro de nível 1 a 4 (níveis 5-6 exigem 1 tributo; níveis 7+ exigem 2). Invoque monstros do Extra Deck, ative Magias e baixe Armadilhas viradas para baixo. Na Battle Phase, declare ataques com monstros em Posição de Ataque. Na Main Phase 2, prepare suas defesas finais.',
    howToWin: 'Quando monstros batalham, se o monstro atacante tiver ATK superior ao monstro defensor em ataque, o monstro defensor é destruído e a diferença é subtraída dos LP do oponente. Ataques diretos causam dano integral aos LP.',
    glossary: [
      { term: 'Pontos de Vida (LP)', definition: 'Sua vitalidade no jogo; você começa com 8000 e perde a partida se chegar a 0.' },
      { term: 'Extra Deck', definition: 'Baralho de até 15 cartas contendo monstros especiais: Fusão, Sincro, Xyz e Link prontos para serem convocados.' },
      { term: 'Invocação-Normal', definition: 'Limite de colocar 1 monstro em campo por turno da mão em Posição de Ataque virado para cima ou Posição de Defesa virado para baixo.' },
      { term: 'Invocação-Especial', definition: 'Invocação de monstros por efeitos de cartas ou materiais do Extra Deck, sem limite de vezes por turno.' },
      { term: 'Armadilha (Trap)', definition: 'Cartas com moldura rosa baixadas viradas para baixo que podem ser disparadas no turno do oponente para surpreendê-lo.' },
      { term: 'Cemitério (GY)', definition: 'Local onde cartas destruídas, tributadas ou usadas repousam e alimentam sinergias.' }
    ],
    formats: ['Advanced TCG', 'Speed Duel', 'Rush Duel', 'Edison / Retro'],
    cardTypes: ['Monstro Normal', 'Monstro de Efeito', 'Fusão', 'Sincro', 'Xyz', 'Link', 'Ritual', 'Pêndulo', 'Magia', 'Armadilha']
  },

  'digimon': {
    id: 2,
    slug: 'digimon',
    name: 'Digimon Card Game',
    gameName: 'Digimon',
    title: 'Como jogar Digimon',
    subtitle: 'Evolua seus parceiros digitais pelo sistema dinâmico de Memory Gauge',
    lead: 'No Digimon Card Game, você é um Tamer conduzindo seus parceiros digitais desde o nascimento como Digitamas até formas Mega supremas, gerenciando o inovador e fluido sistema de Memory Gauge.',
    quickGuide: {
      players: 2,
      handSize: 5,
      victory: 'Destruir a Security e atacar',
      deckSize: '50 cartas + até 5 Digi-Eggs'
    },
    turnSteps: [
      { step: 1, name: 'Unsuspend', desc: 'Desvire todos os seus Digimons e Tamers suspensos em campo.' },
      { step: 2, name: 'Draw', desc: 'Compre 1 carta do deck principal (exceto no 1º turno do jogador inicial).' },
      { step: 3, name: 'Breeding', desc: 'Choque um ovo do Digi-Egg Deck na Área de Criação ou mova um Digimon de nível 3+ chocado para a Área de Batalha.' },
      { step: 4, name: 'Main Phase', desc: 'Digievolua parceiros, jogue Tamers, use cartas de Opção e ataque até o marcador de memória passar de zero.' },
      { step: 5, name: 'End Phase', isDashed: true, desc: 'Se o marcador de memória passar do 0 para o lado do oponente, o turno se encerra após a ação atual.' }
    ],
    turnSubtext: 'Se o marcador de memória passar do 0 para o lado do oponente, o turno se encerra após a ação atual.',
    objective: 'Destruir todos os 5 escudos da Security Stack do adversário e em seguida desferir um ataque direto no Tamer adversário, ou fazê-lo esgotar o baralho.',
    preparation: 'Monte seu Main Deck de 50 cartas e separe de 0 a 5 ovos no Digi-Egg Deck. Ajuste o marcador de memória (Memory Gauge) em 0. Embaralhe e compre 5 cartas. Coloque 5 cartas do topo do baralho na Security Stack viradas para baixo sem olhar.',
    onYourTurn: 'Desvire suas cartas na Unsuspend Phase. Compre 1 carta (exceto jogador 1 no 1º turno). Na Breeding Phase, choque um ovo ou mova um Digimon de Nível 3 ou superior para o campo. Na Main Phase, gaste memória para jogar cartas ou evoluir (digivolver compra 1 carta extra de bônus). Ataque alvos adversários ou a segurança.',
    howToWin: 'Ataques na segurança revelam cartas com Security Effects perigosos. Digimons do topo da pilha defendem com DP. Quando a pilha estiver zerada, um ataque direto sem bloqueio vence a partida!',
    glossary: [
      { term: 'Memory Gauge', definition: 'Barra numérica de -10 a +10 compartilhada; gastar memória move o marcador em direção ao rival.' },
      { term: 'Security Stack', definition: 'Pilha com 5 cartas protetoras que funcionam como os escudos de vida do jogador.' },
      { term: 'Digievolução', definition: 'Sobrepor cartas de nível superior sobre um Digimon prévio, pagando menos memória e comprando +1 carta.' },
      { term: 'Breeding Area (Área de Criação)', definition: 'Zona segura e protegida onde ovos chocam e evoluem sem sofrer efeitos do adversário.' },
      { term: 'Tamer', definition: 'Cartas de treinadores humanos que permanecem no campo garantindo memória extra e efeitos de suporte.' }
    ],
    formats: ['Standard'],
    cardTypes: ['Digimon', 'Digi-Egg (Digitama)', 'Tamer', 'Opção (Option)']
  },

  'star-wars': {
    id: 8,
    slug: 'star-wars',
    name: 'Star Wars: Unlimited',
    gameName: 'Star Wars: Unlimited',
    title: 'Como jogar Star Wars: Unlimited',
    subtitle: 'Batalhe em arenas terrestres e espaciais simultâneas',
    lead: 'Comande naves estelares em órbita e tropas terrestres em solo simultaneamente em um jogo de ritmo eletrizante, troca contínua de iniciativa e estratégia profunda no universo Star Wars.',
    quickGuide: {
      players: 2,
      handSize: 6,
      victory: 'Destruir a Base inimiga (30 HP)',
      deckSize: '50+ cartas + Líder + Base'
    },
    turnSteps: [
      { step: 1, name: 'Fase de Ação (Alternada)', desc: 'Os jogadores alternam turnos executando 1 ação por vez: jogar carta, atacar na Arena Terrestre ou Espacial, usar habilidade ou pegar a Iniciativa.' },
      { step: 2, name: 'Fase de Reagrupamento', desc: 'Compre 2 cartas, escolha 1 carta da mão para colocar como recurso virado para baixo (opcional) e desvire todas as cartas.' }
    ],
    turnSubtext: 'Os duelistas alternam turnos executando 1 ação por vez até que ambos passem a vez.',
    objective: 'Causar 30 de dano à Base do oponente antes que ele destrua a sua base.',
    preparation: 'Coloque seu Líder e sua Base virados para cima. Embaralhe o baralho de 50+ cartas e compre 6. Você pode fazer mulligan (1 vez recomprar as 6). Escolha 2 cartas da mão para colocar como recursos virados para baixo. O jogador inicial recebe o marcador de Iniciativa.',
    onYourTurn: 'Na Fase de Ação, faça 1 ação por vez: jogar uma carta pagando recursos virando-os, atacar com uma unidade na Arena Terrestre ou Espacial, usar uma habilidade de ação ou reivindicar a Iniciativa (para jogar primeiro na próxima rodada). Na Fase de Reagrupamento, compre 2 cartas, baixe até 1 recurso opcional da mão e desvire tudo.',
    howToWin: 'Coordene investidas aéreas e terrestres. O dano às unidades é permanente. Quando a integridade da Base inimiga cair para 0, a vitória é sua!',
    glossary: [
      { term: 'Arena Terrestre & Espacial', definition: 'O campo é dividido em duas frentes independentes: unidades de solo não interagem com naves, permitindo manobras táticas.' },
      { term: 'Recursos', definition: 'Qualquer carta pode ser colocada virada para baixo para se tornar um gerador permanente de 1 recurso.' },
      { term: 'Iniciativa', definition: 'Reivindicar a Iniciativa encerra suas ações na rodada mas garante que você será o primeiro a agir na rodada seguinte.' },
      { term: 'Implantar o Líder (Epic Action)', definition: 'Quando atingir o número necessário de recursos, o Líder vira uma unidade massiva em combate.' }
    ],
    formats: ['Premier', 'Twin Suns (Multiplayer)', 'Draft / Selado'],
    cardTypes: ['Líder', 'Base', 'Unidade Terrestre', 'Unidade Espacial', 'Evento', 'Upgrade']
  },

  'lorcana': {
    id: 14,
    slug: 'lorcana',
    name: 'Disney Lorcana',
    gameName: 'Disney Lorcana',
    title: 'Como jogar Disney Lorcana',
    subtitle: 'Reúna Lore com os Iluminadores e personagens Disney',
    lead: 'Como um Iluminador, use tintas mágicas para conjurar personagens Disney lendários em canções, itens e aventuras para reunir 20 pontos de Lore antes de seus rivais.',
    quickGuide: {
      players: 2,
      handSize: 7,
      victory: 'Alcançar 20 Lore',
      deckSize: '60+ cartas (máx. 2 tintas)'
    },
    turnSteps: [
      { step: 1, name: 'Pronto (Ready)', desc: 'Desvire todas as suas cartas exauridas de volta para a posição pronta (Ready).' },
      { step: 2, name: 'Definir (Set)', isDashed: true, desc: 'A etapa Definir processa efeitos no início do seu turno antes da compra.' },
      { step: 3, name: 'Comprar (Draw)', desc: 'Compre 1 carta do topo do deck (o jogador que começa no 1º turno pula a compra).' },
      { step: 4, name: 'Fase Principal (Main)', desc: 'Coloque 1 carta no tinteiro, invoque personagens/itens/locais, faça Quest por Lore ou desafie personagens exauridos rivais.' }
    ],
    turnSubtext: 'A etapa Definir processa efeitos no início do seu turno antes da compra.',
    objective: 'Ser o primeiro Iluminador a atingir a meta de 20 Lore ou vencer se o oponente precisar comprar de um baralho vazio.',
    preparation: 'Monte seu baralho com no mínimo 60 cartas e no máximo duas cores de tinta (Amber, Amethyst, Emerald, Ruby, Sapphire, Steel). Compre 7 cartas. Você pode alterar qualquer quantidade de cartas da mão no mulligan e recomprar. Ajuste seu rastreador de Lore em 0.',
    onYourTurn: 'Desvire suas cartas exauridas e compre 1 carta (exceto jogador 1 no 1º turno). Uma vez por turno, adicione 1 carta que tenha o símbolo de tinteiro no seu Inkwell virada para baixo como tinta permanente. Na fase principal, gaste tinta para jogar cartas, faça Quest com personagens prontos para ganhar Lore ou desafie personagens exauridos do oponente.',
    howToWin: 'Envie seus personagens em Quests para somar Lore diretamente para o seu total. Proteja seus personagens pontuadores desafiando os rivais e usando canções para controlar a mesa.',
    glossary: [
      { term: 'Lore', definition: 'Os pontos místicos de vitória que você acumula ao enviar personagens em missões (Quests).' },
      { term: 'Tinteiro (Inkwell)', definition: 'Área onde cartas compatíveis são colocadas viradas para baixo como Tinta utilizável para pagar custos.' },
      { term: 'Exaurido / Pronto (Exerted / Ready)', definition: 'Pronto é a posição vertical ativa; Exaurido é a posição virada horizontal após agir.' },
      { term: 'Desafio (Challenge)', definition: 'Atacar diretamente um personagem rival exaurido para aplicar dano mútuo de força e defesa.' },
      { term: 'Shift', definition: 'Mecânica que permite colocar uma versão mais poderosa de um personagem em cima de uma já existente com custo reduzido.' }
    ],
    formats: ['Core Constructed', 'Draft', 'Selado'],
    cardTypes: ['Personagem', 'Ação', 'Canção (Song)', 'Item', 'Local (Location)']
  },

  'fab': {
    id: 11,
    slug: 'fab',
    name: 'Flesh and Blood',
    gameName: 'Flesh and Blood',
    title: 'Como jogar Flesh and Blood',
    subtitle: 'Combate visceral mano a mano em 1v1 com armas, armaduras e cartas de ataque',
    lead: 'Flesh and Blood é um card game de combate corpo a corpo de alta fidelidade onde você veste as armaduras e empunha as armas do seu Herói em duelos épicos de pura destreza tática.',
    quickGuide: {
      players: 2,
      handSize: 4,
      victory: 'Reduzir a Vida do Herói a 0',
      deckSize: '60-80 cartas (Classic) ou 40 (Blitz)'
    },
    turnSteps: [
      { step: 1, name: 'Início do Turno', isDashed: true, desc: 'Efeitos de início de turno são ativados e o jogador ganha 1 Ponto de Ação (Action Point).' },
      { step: 2, name: 'Fase de Ação (Combat Chain)', desc: 'Jogue ataques ou ações descartando cartas na Pitch Zone para pagar recursos; o oponente defende na Combat Chain.' },
      { step: 3, name: 'Fase Final (Recompra)', desc: 'Ambos os jogadores compram a mão cheia no fim da primeira rodada. Nos turnos seguintes, recompra-se até o Intelecto do Herói.' }
    ],
    turnSubtext: 'Ambos os jogadores compram a mão cheia no fim da primeira rodada do jogo.',
    objective: 'Reduzir os pontos de vida do Herói oponente a 0 infligindo dano de combate físico e arcano.',
    preparation: 'Coloque seu Herói no centro, equipe suas Armas e as 4 peças de Equipamento (Cabeça, Tronco, Braços, Pernas). Compre tantas cartas quanto o valor de Intelecto do Herói (geralmente 4 cartas).',
    onYourTurn: 'Você começa com 1 Ponto de Ação (Action Point). Jogue ataques ou ações descartando cartas na Pitch Zone para pagar o custo de recursos (Vermelha = 1, Amarela = 2, Azul = 3). O oponente defende com cartas da mão e equipamentos na Combat Chain. Cartas com Go Again devolvem seu Action Point para continuar a sequência de golpes!',
    howToWin: 'Domine a arte de alternar entre defender e atacar, guarde 1 carta crucial no Arsenal para a rodada seguinte e recicle seu baralho com pitch preciso até que a armadura e a vida do oponente se esgotem.',
    glossary: [
      { term: 'Pitch System', definition: 'Gastar recursos virando cartas que vão para a Pitch Zone e, no fim do turno, para o fundo do seu baralho na ordem que quiser.' },
      { term: 'Combat Chain', definition: 'A sequência encadeada de ataque, bloqueio e reações (Attack Reaction e Defense Reaction).' },
      { term: 'Arsenal', definition: 'Espaço onde você pode guardar 1 carta virada para baixo no fim do turno para jogar em turnos futuros.' },
      { term: 'Go Again', definition: 'Propriedade de ataques e ações que concede de volta 1 Action Point permitindo novos ataques no mesmo turno.' }
    ],
    formats: ['Classic Constructed', 'Blitz', 'Commoner', 'Ultimate Pit Fight (UPF)'],
    cardTypes: ['Herói', 'Arma', 'Equipamento', 'Ação', 'Ataque', 'Reação de Ataque', 'Reação de Defesa', 'Instantânea']
  },

  'dragon-ball-fusion': {
    id: 6,
    slug: 'dragon-ball-fusion',
    name: 'Dragon Ball Super Fusion World',
    gameName: 'Dragon Ball Super Fusion World',
    title: 'Como jogar Dragon Ball Super Fusion World',
    subtitle: 'Desperte o poder do seu Líder e vença combates de alta velocidade',
    lead: 'Enfrente batalhas de anime em alta rotação com guerreiros Z, vilões lendários e o despertar épico do seu Líder quando sua vida estiver por um fio!',
    quickGuide: {
      players: 2,
      handSize: 6,
      victory: 'Destruir as 8 Vidas do Líder',
      deckSize: '50 a 60 cartas + 1 Líder'
    },
    turnSteps: [
      { step: 1, name: 'Charge Phase', desc: 'Desvire todas as cartas e energias. Opcionalmente, baixe 1 carta da mão na Energy Area para gerar energia permanente.' },
      { step: 2, name: 'Draw Phase', desc: 'Compre 1 carta do deck principal (exceto o jogador que começa no primeiro turno).' },
      { step: 3, name: 'Main Phase', desc: 'Convoque Battle Cards pagando energias da cor do Líder, ative cartas Extra, desperte o Líder (Awaken) e ataque!' },
      { step: 4, name: 'End Phase', isDashed: true, desc: 'Resolução de encerramento de efeitos e verificação de condições do fim do turno.' }
    ],
    turnSubtext: 'A Charge Phase permite converter 1 carta da mão em energia permanente.',
    objective: 'Reduzir todas as 8 cartas de Vida do Líder adversário a 0 e desferir o golpe decisivo.',
    preparation: 'Coloque o Líder no espaço central. Embaralhe o baralho e compre 6 cartas. Você pode fazer mulligan uma vez. Coloque 8 cartas do topo na Life Area viradas para baixo.',
    onYourTurn: 'Desvire todas as cartas e adicione opcionalmente 1 carta da mão na Energy Area. Compre 1 carta (exceto jogador 1 no 1º turno). Convoque Battle Cards pagando energias da cor do Líder, ative cartas Extra e ataque. Quando a vida atingir 4 ou menos, vire a carta do Líder para Despertar (Awaken)!',
    howToWin: 'Durante as batalhas, jogue cartas na Combo Area para somar poder (+5000 / +10000). Quem tiver maior poder vence o choque!',
    glossary: [
      { term: 'Awaken (Despertar)', definition: 'Virar a carta do Líder para o lado Desperto quando a Vida chega a 4 ou menos, recebendo mais poder e habilidades supremas.' },
      { term: 'Combo Area', definition: 'Área onde cartas da mão ou campo entram para adicionar poder em ataques ou defesas antes de irem para o descarte.' },
      { term: 'Energy Area', definition: 'Área de energias geradas por cartas da sua própria mão para pagar custos de invocação.' }
    ],
    formats: ['Constructed', 'Selado'],
    cardTypes: ['Líder', 'Battle Card', 'Extra Card']
  },

  'cardfight-vanguard': {
    id: 10,
    slug: 'cardfight-vanguard',
    name: 'Cardfight!! Vanguard',
    gameName: 'Cardfight!! Vanguard',
    title: 'Como jogar Cardfight!! Vanguard',
    subtitle: 'Conduza seus guerreiros pelo Planeta Cray e alcance a vitória transcendental',
    lead: 'Conduza sua unidade Vanguard pelo Planeta Cray subindo de grau, revelando gatilhos decisivos no Drive Check e executando ataques devastadores com seus Rear-guards.',
    quickGuide: {
      players: 2,
      handSize: 5,
      victory: 'Infligir 6 de Dano no Vanguard',
      deckSize: '50 cartas + Ride Deck'
    },
    turnSteps: [
      { step: 1, name: 'Stand', desc: 'Desvire todas as suas unidades em campo de volta para a posição ativa (Stand).' },
      { step: 2, name: 'Draw', desc: 'Compre 1 carta do deck principal.' },
      { step: 3, name: 'Ride', desc: 'Na Ride Phase você evolui o Vanguard descartando 1 carta para subir o grau (Graus 0 a 3) pelo Ride Deck.' },
      { step: 4, name: 'Main', desc: 'Invoque Rear-guards nos círculos de apoio, ative ordens e posicione suas colunas de batalha.' },
      { step: 5, name: 'Battle', desc: 'Declare ataques, execute Drive Checks (Trigger Checks) revelando o topo do baralho e aplique gatilhos!' },
      { step: 6, name: 'End', isDashed: true, desc: 'Encerramento de efeitos temporários da batalha e passagem do turno.' }
    ],
    turnSubtext: 'Na Ride Phase você evolui o Vanguard subindo de grau (Grau 0 a 3).',
    objective: 'Fazer o adversário acumular 6 cartas em sua Damage Zone ou esgotar seu baralho.',
    preparation: 'Coloque seu Vanguard Grau 0 virado para baixo no Vanguard Circle. Separe o Ride Deck (Graus 0 a 3). Compre 5 cartas com direito a mulligan parcial. Ambos gritam "Stand up, Vanguard!" e revelam o Grau 0.',
    onYourTurn: 'Desvire todas as unidades no Stand. Compre 1 carta. Na Ride Phase, descarte 1 carta para colocar o próximo grau do Ride Deck sobre o Vanguard. Invoque Rear-guards na Main Phase. Na Battle Phase, declare ataques, revele Drive Checks do topo do baralho e aplique Triggers!',
    howToWin: 'Gatilhos de Crítico concedem dano duplo, Gatilhos de Cura recuperam dano, e Over Triggers concedem 100 milhões de poder virando duelos perdidos.',
    glossary: [
      { term: 'Vanguard & Rear-guards', definition: 'O Vanguard é seu guerreiro avatar no centro; os Rear-guards são as tropas de apoio nos círculos ao redor.' },
      { term: 'Drive Check', definition: 'Revelar a carta do topo do baralho durante o ataque do Vanguard para verificar se houve ativação de gatilho.' },
      { term: 'Damage Check', definition: 'Ao sofrer um ataque bem-sucedido, revele cartas do topo e coloque na Damage Zone (gatilhos também ativam aqui).' },
      { term: 'Counter Blast', definition: 'Virar cartas da Damage Zone com a face para baixo para pagar custos de habilidades.' }
    ],
    formats: ['Standard (D-Standard)', 'Premium', 'V-Premium'],
    cardTypes: ['Normal Unit', 'Trigger Unit', 'Order Card']
  },

  'altered': {
    id: 17,
    slug: 'altered',
    name: 'Altered TCG',
    gameName: 'Altered',
    title: 'Como jogar Altered',
    subtitle: 'Explore um mundo transformado em uma corrida mágica de expedições simultâneas',
    lead: 'Em Altered, esqueça a violência direta: o objetivo é uma corrida mística de exploração simultânea onde o Herói e seu Companheiro viajam por regiões misteriosas para se reencontrarem na trilha da aventura.',
    quickGuide: {
      players: 2,
      handSize: 6,
      victory: 'Reunir Herói e Companheiro',
      deckSize: '40 a 60 cartas'
    },
    turnSteps: [
      { step: 1, name: 'Manhã (Morning)', desc: 'Desvire todas as suas Mana Orbs, compre 2 cartas e opcionalmente adicione 1 carta da mão como nova Mana Orb.' },
      { step: 2, name: 'Meio-dia (Noon)', isDashed: true, desc: 'Ative habilidades e efeitos específicos desencadeados pelo Meio-dia (Noon).' },
      { step: 3, name: 'Tarde (Afternoon)', desc: 'Jogadores alternam turnos jogando cartas na expedição do Herói ou do Companheiro, ou utilizando a Reserva.' },
      { step: 4, name: 'Entardecer (Dusk)', desc: 'Compare as estatísticas nos biomas da expedição (Floresta, Montanha, Água). Quem vencer avança o marcador na trilha.' },
      { step: 5, name: 'Noite (Night)', isDashed: true, desc: 'Personagens em expedição vão para a Reserva (ou são descartados se forem Fleeting) e limpam o campo.' }
    ],
    turnSubtext: 'O jogo flui pelas partes do dia, com comparações de estatísticas no Entardecer.',
    objective: 'Avançar os marcadores do Herói e do Companheiro através das cartas de região até que ambos se encontrem no mesmo espaço da trilha.',
    preparation: 'Escolha seu Herói e sua facção. Monte a Trilha de Aventura no centro com cartas de região. Coloque o marcador do Herói em uma ponta e o do Companheiro na outra. Compre 6 cartas (mulligan permitido). Baixe 3 cartas da mão como Mana Orbs iniciais.',
    onYourTurn: 'Na Manhã, desvire manas, compre 2 cartas e adicione 1 Mana Orb opcional. Na Tarde, alterne ações jogando cartas na expedição do Herói ou do Companheiro, ou use a Reserva. No Entardecer, compare as estatísticas de Floresta, Montanha e Água: os vencedores avançam marcadores!',
    howToWin: 'Planeje os atributos corretos para cada bioma da região atual e use a Reserva para reutilizar personagens estratégicos por um custo de mana reduzido.',
    glossary: [
      { term: 'Trilha de Aventura', definition: 'Caminho central composto por regiões de Tumult e Biomas onde Herói e Companheiro correm para se encontrar.' },
      { term: 'Reserva', definition: 'Zona para onde personagens vão após o dia terminar, podendo ser jogados novamente no dia seguinte por um custo de reserva mais barato.' },
      { term: 'Mana Orb', definition: 'Cartas da mão convertidas em fontes de energia permanente viradas para baixo.' }
    ],
    formats: ['Constructed', 'Selado', 'Draft'],
    cardTypes: ['Herói', 'Personagem', 'Feitiço', 'Permanente']
  },

  'grand-archive': {
    id: 18,
    slug: 'grand-archive',
    name: 'Grand Archive TCG',
    gameName: 'Grand Archive',
    title: 'Como jogar Grand Archive',
    subtitle: 'Anime TCG focado em materialização precisa, reserva de memória e evolução de classes',
    lead: 'Anime TCG ocidental inovador construído em torno de dois decks: o Material Deck (onde seu Campeão evolui e equipa Regalias) e o Main Deck gerenciado com precisão de memória.',
    quickGuide: {
      players: 2,
      handSize: 7,
      victory: 'Zerar os Pontos de Vida do Campeão',
      deckSize: 'Main 60+ / Material até 12'
    },
    turnSteps: [
      { step: 1, name: 'Wake Up', desc: 'Desvire todas as cartas e recupere armas e aliados exauridos.' },
      { step: 2, name: 'Materialize', desc: 'Materialize 1 carta do seu Material Deck pagando seu Custo Azul (suba o nível do Campeão ou equipe uma Regalia).' },
      { step: 3, name: 'Recollect', isDashed: true, desc: 'Mova cartas da Reserve Pool para a Memory Zone.' },
      { step: 4, name: 'Draw (1 cartas)', desc: 'Compre 1 carta do Main Deck.' },
      { step: 5, name: 'Main Phase', desc: 'Jogue aliados, armas, ações pagando o Custo Amarelo reservando cartas da mão, ative habilidades e declare ataques.' },
      { step: 6, name: 'End Phase', isDashed: true, desc: 'Verificação do limite de retenção de memória e encerramento de efeitos temporários.' }
    ],
    turnSubtext: 'Na Materialize Phase você pode subir o nível do Campeão ou equipar uma arma.',
    objective: 'Reduzir a pontuação de vida do Campeão adversário a 0.',
    preparation: 'Coloque o Campeão Nível 0 no Material Deck (máx. 12 cartas). Embaralhe o Main Deck (mínimo 60 cartas) e compre 7 cartas. Aplique a regra de mulligan padrão.',
    onYourTurn: 'Desvire suas cartas no Wake Up. No Materialize, gaste materialização (Custo Azul) para subir o Campeão de nível ou equipar Regalias. Compre 2 cartas no Draw. Na Main Phase, jogue cartas do Main Deck pagando o Custo Amarelo reservando cartas da mão como memória, e ataque com aliados e armas!',
    howToWin: 'Construa uma progressão veloz de classes, equipe armas de alto impacto e controle o ritmo das cartas reservadas na memória para não ficar sem recursos.',
    glossary: [
      { term: 'Material Deck', definition: 'Baralho de até 12 cartas singleton contendo as evoluções do Campeão e equipamentos lendários (Regalias).' },
      { term: 'Custo Azul vs Custo Amarelo', definition: 'Custo Azul é pago materializando do Material Deck; Custo Amarelo é pago guardando cartas da mão na memória.' },
      { term: 'Nível do Campeão', definition: 'O Campeão evolui do Nv. 0 ao Nv. 3, liberando novas cartas de classe e maior capacidade de combate.' }
    ],
    formats: ['Standard', 'Silver', 'Draft'],
    cardTypes: ['Campeão', 'Regalia', 'Aliado', 'Ação', 'Ataque', 'Item', 'Arma', 'Domínio']
  },

  'cyberpunk': {
    id: 19,
    slug: 'cyberpunk',
    name: 'Cyberpunk TCG',
    gameName: 'Cyberpunk',
    title: 'Como jogar Cyberpunk',
    subtitle: 'Invasões cibernéticas táticas de alta voltagem nas ruas iluminadas a neon de Night City',
    lead: 'Mergulhe no submundo iluminado a neon de Night City, invadindo servidores de megacorporações ou drenando os recursos neurais do rival até causar um Flatline fatal.',
    quickGuide: {
      players: 2,
      handSize: 5,
      victory: '7 Pontos de Agenda ou Flatline',
      deckSize: '40 a 50 cartas + 3 Legends'
    },
    turnSteps: [
      { step: 1, name: 'Boot Phase', desc: 'Restaure nós de RAM descarregados e desvire programas e ciberware.' },
      { step: 2, name: 'Interface Phase', desc: 'Compre 1 carta do Database Deck e ganhe créditos de base.' },
      { step: 3, name: 'Netrun & Action Phase', desc: 'Instale Ciberware, quebre barreiras de ICE com programas de invasão e ataque alvos corporativos ou a rede inimiga.' },
      { step: 4, name: 'Cooling & Purge', isDashed: true, desc: 'A fase de resfriamento (Cooling) descarta sobrecargas de buffer de memória e resfria sistemas sobrecarregados.' }
    ],
    turnSubtext: 'A fase de resfriamento (Cooling) descarta sobrecargas de buffer de memória.',
    objective: 'Completar infiltrações pontuando 7 Agendas ou causar dano neurológico suficiente para zerar a mão e integridade do adversário (Flatline).',
    preparation: 'Selecione 3 Legends distintas que configuram seu pool de RAM e facção. Embaralhe o Database Deck (40-50 cartas) e compre 5 cartas. Mulligan permitido uma vez.',
    onYourTurn: 'Recarregue seus nós de RAM na Boot Phase. Ganhe créditos e compre dados na Interface Phase. Na Action Phase, quebre barreiras corporativas, instale ciberware e ataque servidores inimigos.',
    howToWin: 'Equilibre poder de fogo nas ruas e invasões no ciberespaço, explorando as vulnerabilidades de ICE da rede adversária.',
    glossary: [
      { term: 'Legends Matrix', definition: 'As 3 Legends que representam seus contatos e definem os limites de RAM da sua facção.' },
      { term: 'Flatline', definition: 'Condição de vitória imediata alcançada quando um jogador sofre dano cibernético sem ter cartas na mão para amortecer.' },
      { term: 'ICE & Firewalls', definition: 'Programas de defesa corporativa colocados para impedir invasões nos servidores.' }
    ],
    formats: ['Constructed', 'Quick Netrun'],
    cardTypes: ['Legend', 'Ciberware', 'Programa', 'Operação', 'Servidor', 'ICE']
  },

  'godzilla': {
    id: 20,
    slug: 'godzilla',
    name: 'Godzilla Card Game',
    gameName: 'Godzilla',
    title: 'Como jogar Godzilla',
    subtitle: 'Batalhas titânicas em escala colossal entre os Reis dos Monstros',
    lead: 'Batalhas titânicas em escala colossal entre os Reis dos Monstros, onde edifícios são destruídos para alimentar a fúria radioativa e desencadear rajadas atômicas devastadoras.',
    quickGuide: {
      players: 2,
      handSize: 5,
      victory: 'Destruir as Zonas Urbanas ou Kaiju IV',
      deckSize: '50 cartas + 4 Kaijus (Ranks I-IV)'
    },
    turnSteps: [
      { step: 1, name: 'Rampage Start', desc: 'Desvire monstros e suporte militar e restaure contadores de poder radioativo.' },
      { step: 2, name: 'Draw Phase', desc: 'Compre 1 carta do deck principal.' },
      { step: 3, name: 'Evolution Phase', desc: 'Na Evolution Phase você evolui seu Kaiju para Ranks superiores ao atingir a fúria necessária e baixe forças militares.' },
      { step: 4, name: 'Destruction & Attack', desc: 'Destrua setores urbanos oponentes acumulando contadores ou duele contra o Kaiju adversário.' },
      { step: 5, name: 'Calamity End', isDashed: true, desc: 'Encerramento de efeitos temporários e consolidação da devastação territorial.' }
    ],
    turnSubtext: 'Na Evolution Phase você evolui seu Kaiju para Ranks superiores ao atingir a fúria.',
    objective: 'Aniquilar todos os distritos territoriais do oponente ou abater seu Kaiju Rank IV em duelo direto.',
    preparation: 'Monte seu Monster Deck com exatamente 4 cartas (Ranks I, II, III e IV). Deck Principal de 50 cartas. Coloque o Kaiju Rank I na zona ativa. Compre 5 cartas.',
    onYourTurn: 'Restaure fúria atômica no início da rodada. Compre 1 carta. Evolua o Kaiju se tiver contadores de destruição suficientes. Na fase de ataque, destrua setores da cidade rival ou ataque o Kaiju oponente com sopros de raio.',
    howToWin: 'Cause danos colossais acumulando destruição para destravar os golpes supremos do Kaiju Rank IV.',
    glossary: [
      { term: 'Monster Deck', definition: 'Pilha com 4 cartas do seu monstro titânico, uma para cada Rank de evolução (I a IV).' },
      { term: 'Fúria Radioativa', definition: 'Recursos gerados pelo dano a edifícios usados para pagar rajadas atômicas e evoluções.' },
      { term: 'Zona de Destruição', definition: 'Territórios urbanos que absorvem ataques antes que a vitória seja declarada.' }
    ],
    formats: ['Standard'],
    cardTypes: ['Monster (Kaiju)', 'Militar', 'Evento', 'Instalação Urbana']
  },

  'gundam': {
    id: 5,
    slug: 'gundam',
    name: 'Gundam Card Game',
    gameName: 'Gundam',
    title: 'Como jogar Gundam',
    subtitle: 'Pilote Mobile Suits icônicos em batalhas táticas entre nações estelares',
    lead: 'Comande esquadrões de Mobile Suits em combates táticos interestelares, vinculando pilotos icônicos às suas máquinas e protegendo sua nave-mãe.',
    quickGuide: {
      players: 2,
      handSize: 5,
      victory: 'Destruir os Escudos e a Nave-Mãe',
      deckSize: '50 cartas + 10 Recursos'
    },
    turnSteps: [
      { step: 1, name: 'Recovery Phase', desc: 'Desvire todas as unidades e naves suspensas em campo.' },
      { step: 2, name: 'Draw Phase', desc: 'Compre 1 carta do Main Deck principal.' },
      { step: 3, name: 'Resource Phase', desc: 'A Resource Phase adiciona energia do baralho de recursos para pagar mobilizações e táticas.' },
      { step: 4, name: 'Main Phase', desc: 'Mobilize Mobile Suits, vincule Pilotos (Pilot Link), jogue Comandos táticos e declare ataques de esquadrão.' },
      { step: 5, name: 'End Phase', isDashed: true, desc: 'Efeitos temporários de combate encerram e o turno é passado ao rival.' }
    ],
    turnSubtext: 'A Resource Phase adiciona energia do baralho de recursos para pagar naves e robôs.',
    objective: 'Destruir a base e os escudos da nave-mãe rival ou esgotar suas defesas de Mobile Suits.',
    preparation: 'Main Deck de 50 cartas e Resource Deck de 10 cartas. Compre 5 cartas. Mulligan padrão de 1 vez. Configure seus escudos de base.',
    onYourTurn: 'Recupere unidades suspensas. Compre 1 carta. Baixe 1 recurso do Resource Deck. Na Main Phase, mobilize Mobile Suits, vincule Pilotos (Pilot Link) e ataque esquadrões oponentes.',
    howToWin: 'Combine pilotos especializados com seus Mobile Suits designados para dobrar o poder de penetração de escudo.',
    glossary: [
      { term: 'Mobile Suit', definition: 'Unidades de combate mecha mobilizadas na linha de frente.' },
      { term: 'Pilot Link', definition: 'Vincular cartas de piloto aos Mobile Suits para destravar ataques especiais e maior poder.' },
      { term: 'Resource Deck', definition: 'Deck dedicado de 10 cartas para garantir fluxo regular de energia.' }
    ],
    formats: ['Classic'],
    cardTypes: ['Mobile Suit', 'Piloto', 'Comando', 'Nave / Base', 'Recurso']
  },

  'union-arena': {
    id: 7,
    slug: 'union-arena',
    name: 'Union Arena',
    gameName: 'Union Arena',
    title: 'Como jogar Union Arena',
    subtitle: 'Anime All-Stars colidem em arenas épicas de ação e tática',
    lead: 'Anime All-Stars se encontram em batalhas frenéticas com personagens lendários de mangás e animes, gerenciando Action Points e linhas de energia táticas.',
    quickGuide: {
      players: 2,
      handSize: 5,
      victory: 'Zerar as 7 Vidas e atacar direto',
      deckSize: '50 cartas + 3 AP'
    },
    turnSteps: [
      { step: 1, name: 'Start (Restaurar AP)', desc: 'Desvire todos os personagens e recupere suas 3 cartas de Action Point (AP) para a posição ativa.' },
      { step: 2, name: 'Draw', desc: 'Compre 1 carta do deck principal.' },
      { step: 3, name: 'Movement (Mover Linhas)', desc: 'Na Movement Phase personagens podem se deslocar livremente entre a Linha de Energia e a Front Line.' },
      { step: 4, name: 'Main (Ações & Ataques)', desc: 'Gaste AP para colocar personagens em jogo, usar eventos e declarar ataques com personagens da Front Line.' },
      { step: 5, name: 'End', isDashed: true, desc: 'Finalização de efeitos de combate e verificação de condições de vida.' }
    ],
    turnSubtext: 'Na Movement Phase personagens podem se deslocar entre a Linha de Energia e a Front Line.',
    objective: 'Destruir todas as 7 cartas de vida e acertar um ataque direto conclusivo no adversário.',
    preparation: 'Main Deck de 50 cartas e 3 cartas de AP (Action Point). Separe 7 cartas para a Life Area. Compre 5 cartas.',
    onYourTurn: 'Restaure seus 3 APs. Compre 1 carta. Mova personagens da Linha de Energia para a Front Line. Gaste APs para jogar novas cartas e ataque!',
    howToWin: 'Equilibre personagens gerando energia na retaguarda com atacantes pesados na linha de frente.',
    glossary: [
      { term: 'Action Point (AP)', definition: '3 pontos de ação fornecidos por turno para pagar invocações e eventos.' },
      { term: 'Front Line & Energy Line', definition: 'A Front Line é onde ocorrem ataques e bloqueios; a Energy Line é a retaguarda que gera requisitos de cor.' },
      { term: 'Trigger de Vida', definition: 'Cartas na Life Area com gatilhos especiais ativados quando você recebe dano.' }
    ],
    formats: ['Classic'],
    cardTypes: ['Personagem', 'Evento', 'Campo', 'Action Point']
  },

  'universus': {
    id: 15,
    slug: 'universus',
    name: 'UniVersus',
    gameName: 'UniVersus',
    title: 'Como jogar UniVersus',
    subtitle: 'O sistema universal de luta com as maiores franquias de animes e games',
    lead: 'O clássico sistema universal de luta onde personagens lendários de animes e games duelam em sequências velozes de ataques, bloqueios e checagens no baralho.',
    quickGuide: {
      players: 2,
      handSize: 6,
      victory: 'Zerar a Vitalidade do Lutador',
      deckSize: '50+ cartas + 1 Personagem'
    },
    turnSteps: [
      { step: 1, name: 'Ready Step', desc: 'Desvire todas as fundações e cartas viradas de volta para a posição vertical pronta.' },
      { step: 2, name: 'Review Step', isDashed: true, desc: 'Opcionalmente descarte 1 carta da sua mão para renovar opções.' },
      { step: 3, name: 'Draw Step', desc: 'A cada turno você recompra cartas até o limite de mão definido pelo seu lutador.' },
      { step: 4, name: 'Combat Phase', desc: 'Jogue fundações, ataques e ações na Card Pool passando checagens no topo do deck; oponente bloqueia da mão.' },
      { step: 5, name: 'End Step', isDashed: true, desc: 'Limpe a Card Pool enviando fundações para o Stage e ataques para o descarte ou Momentum.' }
    ],
    turnSubtext: 'A cada turno você recompra cartas até o limite de mão definido pelo seu lutador.',
    objective: 'Reduzir a vitalidade (Health) do lutador oponente a 0.',
    preparation: 'Coloque seu Personagem em jogo (ele define vida, mão e os 3 símbolos de recursos). Baralho com 50+ cartas compatíveis. Compre a mão inicial.',
    onYourTurn: 'Desvire suas fundações. Opcionalmente descarte 1 carta no Review. Compre até o limite de mão. Jogue fundações e declare ataques passando checagens no topo do deck.',
    howToWin: 'Construa uma muralha de fundações para garantir que seus ataques passem nas checagens e bloqueie golpes rivais com cartas da mão.',
    glossary: [
      { term: 'Check & Dificuldade', definition: 'Revelar a carta do topo do deck: se o valor for igual ou maior que a Dificuldade da jogada, o ataque ou fundação é bem-sucedido.' },
      { term: 'Card Pool', definition: 'Fileira temporária onde cartas jogadas nesta rodada ficam acumuladas, aumentando a dificuldade das jogadas seguintes.' },
      { term: 'Momentum', definition: 'Ataques bem-sucedidos colocados debaixo do personagem para pagar custos de super-golpes.' }
    ],
    formats: ['Standard', 'Retro'],
    cardTypes: ['Personagem', 'Ataque', 'Fundação', 'Ação', 'Ativo']
  },

  'hololive': {
    id: 16,
    slug: 'hololive',
    name: 'Hololive Official Card Game',
    gameName: 'Hololive',
    title: 'Como jogar Hololive',
    subtitle: 'Apoie suas VTubers favoritas através de apresentações, colaborações e shows de talento',
    lead: 'Apoie suas VTubers prediletas montando espetáculos de talentos, anexando cartas do Cheer Deck para abastecer shows de palco e vibrando com fãs em concertos épicos.',
    quickGuide: {
      players: 2,
      handSize: 6,
      victory: 'Conquistar 3 Paces de Palco',
      deckSize: '50 cartas + 20 Cheer + 1 Oshi'
    },
    turnSteps: [
      { step: 1, name: 'Reset Step', desc: 'Desvire todos os holomems e retorne recursos ao estado inicial.' },
      { step: 2, name: 'Draw Step', desc: 'Compre 1 carta do deck principal.' },
      { step: 3, name: 'Cheer Step', desc: 'Na Cheer Step cartas do Cheer Deck são reveladas e anexadas aos holomems no palco como energia de show.' },
      { step: 4, name: 'Main Step (Shows & Bloom)', desc: 'Evolua (Bloom) holomems, jogue cartas de apoio/fã, mova membros entre Collab e Backstage e inicie o show de performance.' },
      { step: 5, name: 'End Step', isDashed: true, desc: 'Encerramento de efeitos da rodada musical e passagem do espetáculo.' }
    ],
    turnSubtext: 'Na Cheer Step cartas do Cheer Deck são reveladas e anexadas aos holomems no palco.',
    objective: 'Conquistar 3 pontos de Palco (Stage Paces) através de apresentações ou zerar a Life do Oshi oponente.',
    preparation: 'Coloque sua Oshi na Oshi Zone. Separe o Cheer Deck de 20 cartas. Main Deck de 50 cartas. Compre 6 cartas. Coloque 1 holomem no Center Stage.',
    onYourTurn: 'Desvire seus membros. Compre 1 carta. Revele cartas de Cheer e anexe aos membros do palco. Evolua holomems com cartas Bloom, jogue itens de apoio e inicie a performance musical!',
    howToWin: 'Acelere as evoluções Bloom das suas cantoras para causar grande entusiasmo no público e superar as atrações rivais.',
    glossary: [
      { term: 'Cheer Deck', definition: 'Baralho de 20 cartas de torcida/energia anexadas aos membros para pagar custos de concerto.' },
      { term: 'Center & Collab Stage', definition: 'O centro do palco é a estrela principal; o Collab permite duetos com membros secundários.' },
      { term: 'Bloom', definition: 'Evolução de holomems do Debut para o 1st e 2nd espetáculo com habilidades mais potentes.' }
    ],
    formats: ['Standard'],
    cardTypes: ['Oshi', 'Holomem', 'Cheer', 'Suporte']
  },

  'sorcery': {
    id: 12,
    slug: 'sorcery',
    name: 'Sorcery: Contested Realm',
    gameName: 'Sorcery: Contested Realm',
    title: 'Como jogar Sorcery: Contested Realm',
    subtitle: 'Batalhe em uma grade tática viva inspirada nos jogos clássicos de fantasia dos anos 90',
    lead: 'Reviva a era de ouro da fantasia tática em uma grade viva onde locais físicos e lacaios se movem espacialmente para encurralar e destruir o Avatar inimigo.',
    quickGuide: {
      players: 2,
      handSize: 6,
      victory: "Death's Door + Golpe de Misericórdia",
      deckSize: 'Spellbook 60+ / Atlas 30+'
    },
    turnSteps: [
      { step: 1, name: 'Untap Step', desc: 'Desvire seus lacaios, artefatos e locais na grade tática.' },
      { step: 2, name: 'Upkeep Step', isDashed: true, desc: 'Resolva habilidades e manutenções de início de rodada.' },
      { step: 3, name: 'Draw Step (Spellbook ou Atlas)', desc: 'No Draw você pode escolher comprar do Spellbook (mágicas e lacaios) ou do Atlas (locais e terrenos).' },
      { step: 4, name: 'Main Step (Mover & Conjurar)', desc: 'Jogue 1 Site do Atlas na grade, convoque lacaios, lance feitiços, mova tropas pelas casas da grade e ataque alvos vizinhos.' },
      { step: 5, name: 'End Step', isDashed: true, desc: 'Encerramento de efeitos temporários.' }
    ],
    turnSubtext: 'No Draw você pode escolher comprar do Spellbook (mágicas) ou do Atlas (locais).',
    objective: 'Reduzir a vida do Avatar oponente a 0 (colocando-o na Porta da Morte / Death\'s Door) e acertar 1 dano adicional final nele.',
    preparation: 'Monte a grade (tabuleiro 4x4 ou 5x4). Posicione os Avatares. Embaralhe o Spellbook (60+ cartas) e o Atlas (30+ locais). Compre 3 cartas de cada deck.',
    onYourTurn: 'Desvire suas permanentes. Escolha comprar do Spellbook ou do Atlas. Na fase principal, baixe 1 local do Atlas na grade, convoque lacaios, lance feitiços, mova tropas e ataque.',
    howToWin: 'Posicione seus locais estrategicamente para encurralar o Avatar rival e avance suas tropas mais poderosas para o golpe final.',
    glossary: [
      { term: 'The Grid (A Grade)', definition: 'O tabuleiro onde locais e tropas existem fisicamente em coordenadas espaciais.' },
      { term: "Death's Door", definition: 'Estado em que o Avatar atinge 0 de vida: ele não morre imediatamente, mas qualquer dano subsequente o destrói.' },
      { term: 'Spellbook vs Atlas', definition: 'Dois decks separados: o Spellbook contém magias e lacaios; o Atlas contém os terrenos e locais que geram mana.' }
    ],
    formats: ['Constructed'],
    cardTypes: ['Avatar', 'Site (Local)', 'Lacaio (Minion)', 'Mágica', 'Artefato', 'Aura']
  },

  'riftbound': {
    id: 13,
    slug: 'riftbound',
    name: 'Riftbound',
    gameName: 'Riftbound',
    title: 'Como jogar Riftbound',
    subtitle: 'Comande campeões e canalize runas dimensionais em fendas arcanas',
    lead: 'Canalize energias dimensionais através de runas arcanas e dispute o controle de 3 fendas elementais no centro do campo de batalha.',
    quickGuide: {
      players: 4,
      handSize: 5,
      victory: 'Controlar 2 das 3 Fendas',
      deckSize: '40 cartas + 12 Runas + 3 Battlefields'
    },
    turnSteps: [
      { step: 1, name: 'Awakening Phase', desc: 'Desvire suas cartas e gere essência de invocação a partir do deck de Runas.' },
      { step: 2, name: 'Draw Phase', desc: 'Compre 1 carta do deck principal.' },
      { step: 3, name: 'Rift Control Phase', desc: 'Na Rift Control Phase verifica-se quem domina a maioria das 3 fendas centrais.' },
      { step: 4, name: 'Action Phase', desc: 'Mobilize unidades, use táticas arcanas e ordene investidas às fendas disputadas.' },
      { step: 5, name: 'End Phase', isDashed: true, desc: 'Encerramento da rodada.' }
    ],
    turnSubtext: 'Na Rift Control Phase verifica-se quem domina a maioria das fendas centrais.',
    objective: 'Controlar 2 das 3 Fendas Centrais (Battlefields) ou destruir o núcleo do Campeão oponente.',
    preparation: 'Posicione o Campeão e a Lenda. Separe o Deck de 12 Runas e as 3 Fendas Centrais. Main Deck de 40 cartas. Compre 5 cartas.',
    onYourTurn: 'Gere essência das suas Runas. Compre 1 carta. Dispute o controle das Fendas. Gaste essência para mobilizar tropas e lançar feitiços arcanos.',
    howToWin: 'Mantenha a guarda das fendas com defesas sólidas enquanto prepara ataques relâmpago nas posições enfraquecidas do rival.',
    glossary: [
      { term: 'Fendas (Battlefields)', definition: '3 zonas centrais neutras disputadas por tropas de ambos os lados.' },
      { term: 'Rune Deck', definition: '12 runas que geram a essência necessária para convocar tropas a cada rodada.' }
    ],
    formats: ['Competitive'],
    cardTypes: ['Campeão', 'Lenda', 'Tropa', 'Feitiço', 'Runa', 'Battlefield']
  }
};

export const HOW_TO_PLAY_GUIDES = GAME_RULES;

/**
 * Referências externas e tutoriais oficiais para cada TCG
 */
export const GAME_REFERENCES: Record<string, GameReferences> = {
  'altered': {
    official_rules_url: "https://altered.re/pages/rules",
    tutorial_title: null,
    tutorial_url: null
  },
  'cardfight-vanguard': {
    official_rules_url: null,
    tutorial_title: "[Master the basics in 10 Minutes!] Vanguard Tutorial Video",
    tutorial_url: "https://www.youtube.com/watch?v=mMjVHYI-u-0"
  },
  'cyberpunk': {
    official_rules_url: null,
    tutorial_title: "How To Play Video | Cyberpunk Trading Card Game",
    tutorial_url: "https://www.youtube.com/watch?v=UmrxMPYGDXo"
  },
  'digimon': {
    official_rules_url: "https://en.digimoncard.com/rule/",
    tutorial_title: "Digimon Card Game Rule Trailer Ver.4.0",
    tutorial_url: "https://www.youtube.com/watch?v=ghZYuIi5mu4"
  },
  'lorcana': {
    official_rules_url: "https://www.disneylorcana.com/en-US/resources",
    tutorial_title: "How to Play Disney Lorcana - Welcome",
    tutorial_url: "https://www.youtube.com/watch?v=q9ATaPv3auE"
  },
  'dragon-ball-fusion': {
    official_rules_url: "https://www.dbs-cardgame.com/fw/en/news/01_115.html",
    tutorial_title: "How to play Dragon Ball Super Card Game Fusion World Ver1.10",
    tutorial_url: "https://www.youtube.com/watch?v=y6ut4ihDj0E"
  },
  'fab': {
    official_rules_url: null,
    tutorial_title: "How to Play Flesh and Blood | How to Game with Becca Scott | FAB TCG",
    tutorial_url: "https://www.youtube.com/watch?v=e8wevo5QWiI"
  },
  'godzilla': {
    official_rules_url: null,
    tutorial_title: "Godzilla Card Game Tutorial Video",
    tutorial_url: "https://www.youtube.com/watch?v=N1WpLBTUdSI"
  },
  'grand-archive': {
    official_rules_url: "https://rules.gatcg.com/",
    tutorial_title: "Grand Archive TCG Quick Start Guide",
    tutorial_url: "https://www.youtube.com/watch?v=r74jMgARJL4"
  },
  'gundam': {
    official_rules_url: null,
    tutorial_title: "【GCG】『GUNDAM CARD GAME』How to play and rules",
    tutorial_url: "https://www.youtube.com/watch?v=ru02IkvRin4"
  },
  'hololive': {
    official_rules_url: null,
    tutorial_title: "How To Play Hololive Official Card Game! 【Caravan Learning Video】 #hOCG",
    tutorial_url: "https://www.youtube.com/watch?v=_-9QjVI7vcg"
  },
  'magic': {
    official_rules_url: "https://magic.wizards.com/en/how-to-play",
    tutorial_title: "What is Magic?",
    tutorial_url: "https://www.youtube.com/watch?v=eyvvXZbjR_A"
  },
  'naruto': {
    official_rules_url: null,
    tutorial_title: "【Official】 NARUTO CARD GAME Overview Trailer",
    tutorial_url: "https://www.youtube.com/watch?v=3o_wqkZJfr0"
  },
  'one-piece': {
    official_rules_url: "https://en.onepiece-cardgame.com/pdf/rule_manual.pdf",
    tutorial_title: "[OFFICIAL] Learn how to play the ONE PIECE CARD GAME!",
    tutorial_url: "https://www.youtube.com/watch?v=s8s7vSSb8Tk"
  },
  'palworld': {
    official_rules_url: null,
    tutorial_title: "Palworld OFFICIAL CARD GAME Tutorial Video",
    tutorial_url: "https://www.youtube.com/watch?v=UdbMWxWcMcw"
  },
  'pokemon': {
    official_rules_url: "https://www.pokemon.com/static-assets/content-assets/cms2/pdf/trading-card-game/rulebook/par_rulebook_en.pdf",
    tutorial_title: "Getting Started with the Pokémon TCG",
    tutorial_url: "https://www.youtube.com/watch?v=eKC5PlYoboE"
  },
  'riftbound': {
    official_rules_url: "https://playriftbound.com/en-us/rules-hub/",
    tutorial_title: "Riftbound: League of Legends TCG Learn To Play ft. Good Time Society",
    tutorial_url: "https://www.youtube.com/watch?v=DMXztr0OOXc"
  },
  'shadowverse': {
    official_rules_url: null,
    tutorial_title: "How to play Shadowverse: Evolve",
    tutorial_url: "https://www.youtube.com/watch?v=7MdrwXRrcgw"
  },
  'sorcery': {
    official_rules_url: null,
    tutorial_title: "Sorcery: Contested Realm - How to Game with Becca Scott",
    tutorial_url: "https://www.youtube.com/watch?v=EGhE-3ov1RY"
  },
  'star-wars': {
    official_rules_url: null,
    tutorial_title: "Star Wars™: Unlimited - Intro Battle: Hoth Learn To Play",
    tutorial_url: "https://youtu.be/Hj-j26ciZDg?si=id4SB_SjD24D6mC-"
  },
  'union-arena': {
    official_rules_url: null,
    tutorial_title: "[Official] UNION ARENA Rule Explanation Video (English)",
    tutorial_url: "https://www.youtube.com/watch?v=kegrZXbwxWU"
  },
  'universus': {
    official_rules_url: null,
    tutorial_title: "UniVersus - Official Learn to Play Walkthrough",
    tutorial_url: "https://www.youtube.com/watch?v=bcSojOdcJwI"
  },
  'warlord': {
    official_rules_url: null,
    tutorial_title: "Warlord CCG - Quick Start Rules",
    tutorial_url: "https://www.youtube.com/watch?v=FRxprbZhBcQ"
  },
  'weiss-schwarz': {
    official_rules_url: null,
    tutorial_title: "How to play Weiss Schwarz in 6 minutes! | Official Weiss Schwarz TCG Tutorial",
    tutorial_url: "https://www.youtube.com/watch?v=tc1gri2xUWo"
  },
  'yugioh': {
    official_rules_url: "https://img.yugioh-card.com/en/downloads/rulebook/YS17_BG_EN.pdf",
    tutorial_title: "Yu-Gi-Oh! For Beginners - Getting Started",
    tutorial_url: "https://youtu.be/orVzLmJhi2c?si=Gx5Sobvuy4uP5k8l"
  }
};

/**
 * Retorna as referências externas de um jogo
 */
export function getGameReferences(slugOrId: string | number | null | undefined): GameReferences | null {
  if (slugOrId === null || slugOrId === undefined || slugOrId === '') return null;
  const canonical = getGameSlug(slugOrId);
  const key = canonical || String(slugOrId).toLowerCase().trim();
  return GAME_REFERENCES[key] || null;
}

/**
 * Busca as regras e guia de jogo pelo ID, slug ou nome do card game
 */
export function getGameRule(slugOrId: string | number | null | undefined): GameRuleDetail | null {
  if (slugOrId === null || slugOrId === undefined || slugOrId === '') return null;
  const canonical = getGameSlug(slugOrId);
  let rule = (canonical && GAME_RULES[canonical]) ? GAME_RULES[canonical] : null;
  if (!rule) {
    const rawKey = String(slugOrId).toLowerCase().trim();
    if (GAME_RULES[rawKey]) {
      rule = GAME_RULES[rawKey];
    }
  }
  if (!rule) return null;
  const key = canonical || rule.slug;
  const refs = GAME_REFERENCES[key] || rule.references || null;
  return {
    ...rule,
    references: refs || rule.references
  };
}

export const getHowToPlayGuide = getGameRule;

/**
 * Retorna todas as regras e guias de jogos registrados
 */
export function getAllGameRules(): GameRuleDetail[] {
  return Object.values(GAME_RULES);
}

export const getAllHowToPlayGuides = getAllGameRules;

/**
 * Retorna os dados combinados com CARD_GAMES
 */
export function getAvailableGamesWithRules() {
  return CARD_GAMES.map(game => ({
    ...game,
    hasRules: !!GAME_RULES[game.slug],
    rules: GAME_RULES[game.slug] || null
  }));
}
